import { computedFn } from '@api'
import { AlignStyle } from '@modules/whiteboard/types'
import { Utils } from '@tldraw/core'

/* ------------------ Shape Styles ------------------ */

export enum ColorStyle {
  White = 'white',
  LightGray = 'lightGray',
  Gray = 'gray',
  Black = 'black',
  Green = 'green',
  Cyan = 'cyan',
  Blue = 'blue',
  Indigo = 'indigo',
  Violet = 'violet',
  Red = 'red',
  Orange = 'orange',
  Yellow = 'yellow',
}

export enum SizeStyle {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export enum DashStyle {
  Draw = 'draw',
  Solid = 'solid',
  Dashed = 'dashed',
  Dotted = 'dotted',
}

export enum FontSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  ExtraLarge = 'extraLarge',
}

export enum FontStyle {
  Script = 'script',
  Sans = 'sans',
  Serif = 'erif',
  Mono = 'mono',
}

export type ShapeStyles = {
  textColor?: ColorStyle | string
  strokeColor?: ColorStyle | string
  backgroundColor?: string
  size?: SizeStyle
  dash?: DashStyle
  font?: FontStyle
  fontSize?: number
  textAlign?: AlignStyle
  isFilled?: boolean
  scale?: number
  strokeWidth?: number
  fontStyle?: FontStyles
  opacity?: number
}

export interface FontStyles {
  bold?: boolean
  italic?: boolean
  underline?: boolean
  strikethrough?: boolean
}
type Theme = string

const canvasLight = '#fafafa'

const canvasDark = '#343d45'

const colors = {
  [ColorStyle.White]: '#f0f1f3',
  [ColorStyle.LightGray]: '#c6cbd1',
  [ColorStyle.Gray]: '#788492',
  [ColorStyle.Black]: '#1d1d1d',
  [ColorStyle.Green]: '#36b24d',
  [ColorStyle.Cyan]: '#0e98ad',
  [ColorStyle.Blue]: '#1c7ed6',
  [ColorStyle.Indigo]: '#4263eb',
  [ColorStyle.Violet]: '#7746f1',
  [ColorStyle.Red]: '#ff2133',
  [ColorStyle.Orange]: '#ff9433',
  [ColorStyle.Yellow]: '#ffc936',
}

export const availableShapeColors = _.values(colors)

export const stickyFills: Record<Theme, Record<ColorStyle, string>> = {
  light: {
    ...(Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, Utils.lerpColor(v, canvasLight, 0.45)])
    ) as Record<ColorStyle, string>),
    [ColorStyle.White]: '#ffffff',
    [ColorStyle.Black]: '#3d3d3d',
  },
  dark: {
    ...(Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [
        k,
        Utils.lerpColor(Utils.lerpColor(v, '#999999', 0.3), canvasDark, 0.4),
      ])
    ) as Record<ColorStyle, string>),
    [ColorStyle.White]: '#1d1d1d',
    [ColorStyle.Black]: '#bbbbbb',
  },
}

export const strokes: Record<Theme, Record<ColorStyle, string>> = {
  light: {
    ...colors,
    [ColorStyle.White]: '#1d1d1d',
  },
  dark: {
    ...(Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, Utils.lerpColor(v, canvasDark, 0.1)])
    ) as Record<ColorStyle, string>),
    [ColorStyle.White]: '#cecece',
    [ColorStyle.Black]: '#cecece',
  },
}

export const fills: Record<Theme, Record<ColorStyle, string>> = {
  light: {
    ...(Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, Utils.lerpColor(v, canvasLight, 0.82)])
    ) as Record<ColorStyle, string>),
    [ColorStyle.White]: '#fefefe',
    [ColorStyle.Black]: '#4d4d4d',
  },
  dark: {
    ...(Object.fromEntries(
      Object.entries(colors).map(([k, v]) => [k, Utils.lerpColor(v, canvasDark, 0.618)])
    ) as Record<ColorStyle, string>),
    [ColorStyle.White]: 'rgb(30,33,37)',
    [ColorStyle.Black]: '#1e1e1f',
  },
}

const strokeWidths = {
  [SizeStyle.Small]: 2,
  [SizeStyle.Medium]: 3.5,
  [SizeStyle.Large]: 5,
}

const fontSizes = {
  [SizeStyle.Small]: 28,
  [SizeStyle.Medium]: 48,
  [SizeStyle.Large]: 96,
  auto: 'auto',
}

const fontFaces = {
  [FontStyle.Script]: '"Caveat Brush"',
  [FontStyle.Sans]: '"Source Sans Pro", sans-serif',
  [FontStyle.Serif]: '"Source Serif Pro", serif',
  [FontStyle.Mono]: '"Source Code Pro", monospace',
}

const fontSizeModifiers = {
  [FontStyle.Script]: 1,
  [FontStyle.Sans]: 1,
  [FontStyle.Serif]: 1,
  [FontStyle.Mono]: 1,
}

const stickyFontSizes = {
  [SizeStyle.Small]: 24,
  [SizeStyle.Medium]: 36,
  [SizeStyle.Large]: 48,
  auto: 'auto',
}

export function getStrokeWidth(size: SizeStyle): number {
  return strokeWidths[size]
}

export function _getFontSize(size: SizeStyle, fontStyle: FontStyle = FontStyle.Script): number {
  return fontSizes[size] * fontSizeModifiers[fontStyle]
}

export function getFontFace(font: FontStyle = FontStyle.Script): string {
  return fontFaces[font]
}

export function getStickyFontSize(size: SizeStyle): number {
  return stickyFontSizes[size]
}

export const getFontStyle = computedFn((style: ShapeStyles): string => {
  const fontSize = getFontSize(style)
  const fontFace = getFontFace(style.font)
  const { scale = 1 } = style

  return `${fontSize}/1.3 ${fontFace}`
})

export const getFontSize = (style: ShapeStyles): string => {
  const fontSize = style.fontSize ?? getStickyFontSize(style.size),
    {scale = 2} = style

  return `${style.fontSize ?? (fontSize * scale)}px`
}

export const getStickyFontStyle = computedFn((style: ShapeStyles): string => {
  const fontSize = getFontSize(style), fontFace = getFontFace(style.font)

  return `${fontSize}/1.3 ${fontFace}`
})

// export function getStickyShapeStyle(style: ShapeStyles, isDarkMode = false) {
//   const { textColor } = style
//
//   const theme: Theme = isDarkMode ? 'dark' : 'light'
//   const adjustedColor = textColor === ColorStyle.Black ? ColorStyle.Yellow : textColor
//
//   return {
//     fill: stickyFills[theme][adjustedColor],
//     stroke: strokes[theme][adjustedColor],
//     color: isDarkMode ? '#1d1d1d' : '#0d0d0d',
//   }
// }
//
//
// export function getShapeStyle(style: Partial<ShapeStyles>, isDarkMode?: boolean) {
//   return getShapeStyleMemoized(style, false)
// }
//
// export const getShapeStyleMemoized = computedFn(function getShapeStyle(
//   style: Partial<ShapeStyles>,
//   isDarkMode?: boolean
// ): {
//   stroke: string
//   fill: string
//   strokeWidth: number
// } {
//   const { strokeColor, textColor, backgroundColor, size, isFilled, strokeWidth } = style ?? createDefaultStyle()
//
//   const finalWidth = strokeWidth ?? getStrokeWidth(size)
//
//   const theme: Theme = isDarkMode ? 'dark' : 'light'
//
//   return {
//     stroke: strokes[theme][strokeColor ?? textColor],
//     fill: isFilled ? fills[theme][backgroundColor ?? strokeColor] : 'none',
//     strokeWidth: finalWidth,
//   }
// })

export const createDefaultStyle = (): ShapeStyles => ({
  textColor: DEFAULT_TEXT_COLOR,
  strokeColor: DEFAULT_STROKE_COLOR,
  strokeWidth: DEFAULT_STROKE_WIDTH,
  size: SizeStyle.Small,
  isFilled: false,
  dash: DashStyle.Draw,
  scale: 1,
  opacity: 1
})

export const createDefaultTextStyle = (): ShapeStyles => ({
  ...createDefaultStyle(),
  font: FontStyle.Script,
  textAlign: 'start',
})

export const DEFAULT_STROKE_COLOR = ColorStyle.Black
export const DEFAULT_TEXT_COLOR = ColorStyle.Black
export const DEFAULT_STROKE_WIDTH = 3


// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/shared/shape-styles.ts